import type {
  ChronicleDeviceRecordingInfo,
  ChronicleLiveOffline,
  ChronicleRecordingEndReason,
} from '@/atoms/chronicle';
import type {
  ChronicleDisconnectCause,
  ChronicleLastStopReason,
  DeviceState,
} from '@/tauri/chronicle/types';

export interface RecordingDropContext {
  wasRecording: boolean;
  isRemoteSyncAsyncEnabled: boolean;
  recordingInfo: ChronicleDeviceRecordingInfo | null;
  hasLiveOffline: boolean;
  /** UI timer seconds — frozen while paused, so always the correct seed. */
  timerSeconds: number;
  anchorMs: number;
  /** False only for a `connected` payload whose post-connect `APP&STE` hasn't
   *  resolved yet (APP-7879) — such a broadcast must not release a hold. */
  recordingStateResolved: boolean;
  /** true in the post-reconnect window (recovered event applied,
   *  awaiting a manual stop). The device is recording again, so a coincident
   *  `connected`/`syncing` broadcast must NOT finalize the live session. */
  reconnectedPendingEnd: boolean;
  /** true only for a `connected` payload whose `recordingPreExisting`
   *  is set — i.e. the device's STE round-trip resolved it as STILL recording a
   *  recovered/pre-existing recording (recovered recordings surface as
   *  `connected`, not `recording`). A genuine long-press stop clears it
   *  (`recording_pre_existing = false`), which is how the reconnect window tells
   *  "still recording" apart from "user ended on the device". */
  recordingPreExisting: boolean;
  /** The `reason` off a `disconnected` payload. Refines how the drop itself is
   *  classified — a deliberate low-battery shutdown is not an interruption. */
  disconnectCause?: ChronicleDisconnectCause | (string & {}) | null;
  /** Why the most recent on-device stop happened. Refines a clean connected/syncing stop. */
  lastStopReason?: ChronicleLastStopReason | null;
}

export type RecordingDropResult =
  /** Hold live-offline: keep the recording info atoms untouched, skip the end
   *  reason. `liveOffline` is null when already held (no re-anchoring). */
  | { action: 'hold'; liveOffline: ChronicleLiveOffline | null }
  /** Finalize as before this feature; `endReason` null when not recording.
   *  `clearLiveOffline` releases a held live-offline (STE&0 exit, APP-7868). */
  | {
      action: 'finalize';
      endReason: ChronicleRecordingEndReason | null;
      clearLiveOffline: boolean;
    };

/** Project the two STE-derived flags off a `state_changed` payload for the drop
 *  decision. `recordingStateResolved` gates a held live-offline exit — only a
 *  resolved `connected` may finalize, and non-`connected` payloads are always
 *  treated resolved (flag-off release must still finalize). `recordingPreExisting`
 *  marks a resolved `connected` whose device is STILL recording a recovered
 *  recording — the signal that tells a genuine long-press stop (pre-existing
 *  false) apart from a still-recording broadcast inside the reconnect window. */
export function deriveSteFlags(payload: DeviceState): {
  recordingStateResolved: boolean;
  recordingPreExisting: boolean;
} {
  return {
    recordingStateResolved:
      payload.type !== 'connected' || payload.recordingStateResolved === true,
    recordingPreExisting:
      payload.type === 'connected' && payload.recordingPreExisting === true,
  };
}

/** Pull stop-cause fields off a `state_changed` payload for `processRecordingDrop`. */
export function extractStopCauses(payload: DeviceState): {
  disconnectCause: RecordingDropContext['disconnectCause'];
  lastStopReason: RecordingDropContext['lastStopReason'];
} {
  return {
    disconnectCause: payload.type === 'disconnected' ? (payload.reason ?? null) : null,
    lastStopReason:
      payload.type === 'connected' ? (payload.lastStopReason ?? null) : null,
  };
}

/** Map a `recording` state payload to the recording-info atom shape. Shared by
 *  the provider's initial-state fetch and its state_changed handler so the field
 *  mapping lives in exactly one place. */
export function recordingInfoFromPayload(
  payload: Extract<DeviceState, { type: 'recording' }>
): ChronicleDeviceRecordingInfo {
  return {
    deviceId: payload.deviceId,
    sessionId: payload.sessionId,
    startedAt: payload.startedAt,
    isPaused: payload.isPaused,
    fileName: payload.fileName,
  };
}

/** BUG A: the paused state to mirror onto `chronicleDeviceRecordingInfoAtom`
 *  when a recovered recording pauses/resumes in the reconnected window. Such a
 *  recording is serialized as `connected` with `recordingPreExisting` set (never
 *  `recording`), so a device pause/resume arrives via a `connected` payload's
 *  `isPaused` and never reaches the `recording` branch that normally tracks it.
 *  Returns the updated recording-info (paused toggled), or `null` when there is
 *  nothing to change — non-`connected` payload, no `isPaused` carried, no active
 *  recording-info (a finalize nulled it), or already at that paused value. */
export function resolveReconnectPausedInfo(
  payload: DeviceState,
  current: ChronicleDeviceRecordingInfo | null
): ChronicleDeviceRecordingInfo | null {
  if (payload.type !== 'connected' || payload.isPaused === undefined) return null;
  if (!current || current.isPaused === payload.isPaused) return null;
  return { ...current, isPaused: payload.isPaused };
}

/** Decide how a non-recording state event affects the active recording
 *  (APP-7866). Pure function, mirroring `processStateEvent`'s pattern. */
export function processRecordingDrop(
  eventType: DeviceState['type'],
  context: RecordingDropContext
): RecordingDropResult {
  // A deliberate low-battery shutdown: the device stops recording and saves the
  // file before powering off, so there is nothing to hold a reconnect for —
  // finalize under its own end reason. Recovery-window drops keep hold semantics.
  if (
    eventType === 'disconnected' &&
    context.disconnectCause === 'poweroff_batlow' &&
    !context.hasLiveOffline &&
    !context.reconnectedPendingEnd
  ) {
    return {
      action: 'finalize',
      endReason: context.wasRecording ? 'poweroff_batlow' : null,
      clearLiveOffline: false,
    };
  }

  // Cap attribution (`max_duration` endReason) is connected-only: extractStopCauses
  // only reads lastStopReason from connected payloads (Syncing DTO has no such field).
  const isNormalStop = eventType === 'connected' || eventType === 'syncing';

  if (context.isRemoteSyncAsyncEnabled && context.wasRecording && !isNormalStop) {
    if (context.hasLiveOffline) {
      return { action: 'hold', liveOffline: null };
    }
    return {
      action: 'hold',
      liveOffline: {
        sessionId: context.recordingInfo?.sessionId ?? null,
        fileName: context.recordingInfo?.fileName ?? null,
        lastSeedSecs: context.timerSeconds,
        anchorMs: context.anchorMs,
        isPaused: context.recordingInfo?.isPaused ?? false,
      },
    };
  }

  if (context.hasLiveOffline) {
    if (context.isRemoteSyncAsyncEnabled && !context.recordingStateResolved) {
      // Reconnect race (APP-7879): `connected` (Idle) broadcasts before the
      // device answered APP&STE — keep holding until the state resolves.
      // Flag-off is exempt so the kill switch can always release a hold.
      return { action: 'hold', liveOffline: null };
    }
    // a RESOLVED `connected` that still reports a pre-existing
    // recording is the reconnect-while-recording case — the device is recording
    // again (recovered recordings serialize as `connected` with
    // `recordingPreExisting` set, never `recording`), and `recording_recovered`
    // (which arms the reconnected window) fires LATER, while this hold is still
    // set. Keep holding: finalizing here would null the recording-info atom,
    // trip the device-stop detector, and spuriously interrupt the still-live
    // session. This is the same `recordingPreExisting` distinguisher the
    // reconnected-pending-end branch below uses to tell "still recording" apart
    // from a genuine on-device stop.
    if (context.isRemoteSyncAsyncEnabled && context.recordingPreExisting) {
      return { action: 'hold', liveOffline: null };
    }
    // STE&0 exit: device is back but no longer recording (finished
    // while disconnected) and no recovered event routed it. Always
    // `ble_disconnect` interruption so stop keeps sync_tracking + on-device
    // file for bulk-sync (post-drop audio exists only on the device). Cap
    // attribution (HAR-1311) is via sticky `lastStopReason=max_duration` for
    // the toast / stop-pipeline `stop_reason` — do NOT reclassify endReason
    // as `max_duration` here: that maps to clean-stop cleanup and drops the
    // device-file fallback. Checking `recordingPreExisting` above (rather
    // than dropping this finalize) preserves the still-recording reconnect path.
    return {
      action: 'finalize',
      endReason: 'ble_disconnect',
      clearLiveOffline: true,
    };
  }

  // after a recovered reconnect the hold is cleared but the device is
  // recording again, pending an end. Most coincident non-recording broadcasts
  // must NOT finalize the still-live session: a `syncing` echo, or a `connected`
  // emitted while the online segment finalizes/uploads (which flips the note-gen
  // to COMPLETED), or an unresolved `connected` (STE round-trip pending — the
  // device may still be recording), or a resolved `connected` that reports the
  // device is STILL recording a pre-existing recording. Finalizing any of those
  // would null the recording-info atom, trip the device-stop detector, and drop
  // the reconnected widget prematurely — so they hold (no-op).
  //
  // The ONE exception is a GENUINE device stop: a `connected` whose post-connect
  // APP&STE round-trip has RESOLVED the device to no-longer-recording
  // (`recordingStateResolved` true AND `recordingPreExisting` false) means the
  // user long-pressed to end on the device. Let it finalize as a clean
  // `normal_stop` so the desktop session ends and hands off to the bulk-sync
  // recovery flow (the device-stop detector then runs the non-interrupted stop).
  // A genuine second BLE drop (`!isNormalStop`) still re-holds via the block above.
  if (context.isRemoteSyncAsyncEnabled && context.reconnectedPendingEnd && isNormalStop) {
    const isGenuineDeviceStop =
      eventType === 'connected' &&
      context.recordingStateResolved &&
      !context.recordingPreExisting;
    if (!isGenuineDeviceStop) {
      return { action: 'hold', liveOffline: null };
    }
    // fall through to finalize below — `endReason` resolves to 'normal_stop'.
  }

  let endReason: ChronicleRecordingEndReason | null = null;
  if (context.wasRecording) {
    if (isNormalStop && context.lastStopReason === 'max_duration') {
      endReason = 'max_duration';
    } else {
      endReason = isNormalStop ? 'normal_stop' : 'ble_disconnect';
    }
  }
  return { action: 'finalize', endReason, clearLiveOffline: false };
}
